import './index.css';

/**
 * apps/katalog-app/src/main.tsx
 * Katalog app entry point with proper theme and provider setup.
 */

// Import app CSS with all tokens (self-contained, no external CSS imports)
// CRITICAL: Set theme attribute FIRST, before any imports or rendering
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('data-theme', 'katalog');

  try {
    const storedTheme = localStorage.getItem('bright-ui:theme');
    if (storedTheme && ['koordinator', 'kloutforce', 'katalog', 'admin'].includes(storedTheme)) {
      document.documentElement.setAttribute('data-theme', storedTheme);
    }
  } catch {
    /* intentionally empty - localStorage not available, keep default */
  }
}

// Stale-chunk recovery: when a tab is left open across a deploy, dynamic
// imports may 404 against the previous build's hashed chunks. Reload once
// (guarded by sessionStorage to prevent infinite loops) so the user lands
// on the new build silently instead of seeing a hard error screen.
if (typeof window !== 'undefined') {
  const RELOAD_KEY = 'katalog:vite-preload-reload';
  const STALE_CHUNK_RE =
    /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk|ChunkLoadError/i;
  const recover = (event?: Event) => {
    event?.preventDefault?.();
    try {
      if (!sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, '1');
        window.location.reload();
      } else {
        sessionStorage.removeItem(RELOAD_KEY);
      }
    } catch {
      /* sessionStorage unavailable */
    }
  };
  window.addEventListener('vite:preloadError', recover as EventListener);
  window.addEventListener('error', (e) => {
    if (STALE_CHUNK_RE.test(String((e as ErrorEvent).message || ''))) recover(e);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r: any = (e as PromiseRejectionEvent).reason;
    if (STALE_CHUNK_RE.test(String(r?.message || r || ''))) recover(e);
  });
}

import React from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, ToastProvider } from '@bright-web-skeleton/ui';
import { AuthProvider } from '@bright-web-skeleton/auth';
import { LovableProvider, initTagger } from '@integrations/lovable';
import { Toaster as SonnerToaster } from 'sonner';

import router from './app/router';
import { SignupIntentHealer } from './components/SignupIntentHealer';

// Initialize Lovable tagger (dev mode)
if (typeof window !== 'undefined') {
  initTagger();
}

const queryClient = new QueryClient();

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="katalog">
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider appId="katalog">
            <LovableProvider>
              <SignupIntentHealer />
              <RouterProvider router={router} />
              <SonnerToaster richColors position="top-center" closeButton />
            </LovableProvider>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
